import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface TokenValidationResponse {
  valid: boolean;
  message?: string;
  expiresAt?: number;
}

const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false);
  const isValidating = ref(false);
  const authError = ref<string | null>(null);
  const tokenExpiresAt = ref<number | null>(null);
  const accessToken = ref<string | null>(null);

  // URL del backend para validar tokens - configurable via env
  const validationEndpoint = import.meta.env.VITE_TOKEN_VALIDATION_URL || '';
  
  // Tiempo de expiración del token en segundos (default: 5 minutos)
  const tokenExpirationSeconds = Number(import.meta.env.VITE_TOKEN_EXPIRATION_SECONDS) || 300;

  const isTokenExpired = computed(() => {
    if (!tokenExpiresAt.value) return true;
    return Date.now() > tokenExpiresAt.value;
  });

  const extractTokenFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('accessToken') || urlParams.get('access_token');
  };

  const extractTimestampFromUrl = (): number | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const ts = urlParams.get('ts') || urlParams.get('timestamp');
    return ts ? Number(ts) : null;
  };

  const validateTokenLocally = (token: string, timestamp: number | null): boolean => {
    if (!token) return false;
    
    // Si hay timestamp, verificar que no haya expirado
    if (timestamp) {
      const now = Date.now();
      const tokenAge = now - timestamp;
      const maxAge = tokenExpirationSeconds * 1000;
      
      if (tokenAge > maxAge) {
        authError.value = 'El token ha expirado';
        return false;
      }
    }
    
    return true;
  };

  const validateTokenWithBackend = async (token: string): Promise<boolean> => {
    if (!validationEndpoint) {
      // Si no hay endpoint configurado, solo validar localmente
      return true;
    }

    try {
      const response = await fetch(validationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        authError.value = 'Error al validar el token con el servidor';
        return false;
      }

      const data: TokenValidationResponse = await response.json();
      
      if (!data.valid) {
        authError.value = data.message || 'Token inválido';
        return false;
      }

      if (data.expiresAt) {
        tokenExpiresAt.value = data.expiresAt;
      }

      return true;
    } catch (error) {
      authError.value = 'Error de conexión al validar el token';
      return false;
    }
  };

  const validateAccess = async (): Promise<boolean> => {
    isValidating.value = true;
    authError.value = null;

    try {
      const token = extractTokenFromUrl();
      const timestamp = extractTimestampFromUrl();

      if (!token) {
        authError.value = 'No se proporcionó token de acceso';
        isAuthenticated.value = false;
        return false;
      }

      // Validación local (timestamp/expiración)
      if (!validateTokenLocally(token, timestamp)) {
        isAuthenticated.value = false;
        return false;
      }

      // Validación con backend
      const isValidBackend = await validateTokenWithBackend(token);
      if (!isValidBackend) {
        isAuthenticated.value = false;
        return false;
      }

      // Token válido
      accessToken.value = token;
      isAuthenticated.value = true;
      
      // Si no hay expiresAt del backend, calcular basado en timestamp
      if (!tokenExpiresAt.value && timestamp) {
        tokenExpiresAt.value = timestamp + (tokenExpirationSeconds * 1000);
      }

      // Limpiar token de la URL por seguridad
      stripTokenFromUrl();

      return true;
    } finally {
      isValidating.value = false;
    }
  };

  const stripTokenFromUrl = () => {
    const url = new URL(window.location.toString());
    url.searchParams.delete('accessToken');
    url.searchParams.delete('access_token');
    url.searchParams.delete('ts');
    url.searchParams.delete('timestamp');
    window.history.replaceState(null, '', url.toString());
  };

  const logout = () => {
    isAuthenticated.value = false;
    accessToken.value = null;
    tokenExpiresAt.value = null;
    authError.value = null;
  };

  return {
    isAuthenticated,
    isValidating,
    authError,
    tokenExpiresAt,
    accessToken,
    isTokenExpired,
    validateAccess,
    logout,
  };
});

export default useAuthStore;
